/**
 * Icon Configuration - Maps emoji names to Material Symbols icon names
 * Colors are optimized for both light and dark modes
 */

export const iconMap = {
  navigation: {
    home_dashboard: "home",
    admin_panel_super_admin: "admin_panel_settings",
    settings: "settings",
    profile: "person",
    calendar_book_service: "calendar_month",
    bikes_and_services: "directions_bike",
    service_shops: "store",
    my_bike_passports: "badge",
    time_off: "beach_access",
    billing: "credit_card",
    help: "help",
    support: "support_agent",
    logout: "logout",
    light_mode: "light_mode",
    dark_mode: "dark_mode"
  },
  status_feedback: {
    success: "check_circle",
    error_inactive: "cancel",
    warning: "warning",
    info: "info",
    success_completion: "celebration",
    in_progress_uploading_processing: "hourglass_top",
    security_trial_protection: "shield"
  },
  bike_service_domain: {
    bike_passport: "badge",
    cyclist: "person",
    bike: "directions_bike",
    service_repair: "build",
    technician_mechanic: "engineering",
    details_clipboard: "assignment",
    documents_receipts: "description",
    photos: "photo_camera",
    security_ownership_verification: "verified_user",
    login_credentials_password: "key",
    lock_security: "lock"
  },
  communication_notifications: {
    messages_chat: "chat",
    email_sync_campaigns: "mail",
    notifications_reminders: "notifications",
    send_email: "send"
  },
  business_admin: {
    analytics_statistics: "analytics",
    statistics_up_assumed: "trending_up",
    statistics_down_assumed: "trending_down",
    users_customers: "group",
    companies: "apartment",
    marketing_campaigns: "campaign",
    payment_billing: "payments"
  },
  calendar_time: {
    calendar: "calendar_month",
    calendar_alternative: "event",
    calendar_date_picker: "date_range",
    time_duration_buffer: "schedule"
  },
  reviews_results: {
    rating_star: "star",
    featured_review_popular: "stars",
    top_rated_achievement: "military_tech",
    perfect_score: "verified"
  },
  actions_operations: {
    edit: "edit",
    write_signature_review: "draw",
    view: "visibility",
    start_play: "play_arrow",
    upload_upgrade: "upload",
    download_assumed: "download",
    delete: "delete",
    resume_restore: "undo",
    switch_toggle: "swap_horiz",
    copy: "content_copy"
  },
  features_services: {
    launch_start_app: "rocket_launch",
    target_try_it_now: "target",
    integration: "hub",
    empty_state: "inbox",
    building_creating: "construction"
  },
  misc: {
    map: "map",
    location: "location_on",
    elevation_gain: "terrain",
    heart_rate: "favorite",
    tag_discount_label: "sell",
    design_system_documentation: "palette",
    phone: "call"
  },
  backend_scripts: {
    log_success: "check_circle",
    log_error: "cancel",
    log_warning: "warning",
    log_info: "info",
    log_success_completion: "celebration",
    log_email_ops: "mail",
    log_update: "edit",
    log_delete: "delete",
    log_script_launch: "rocket_launch",
    log_statistics: "analytics",
    log_companies: "apartment",
    log_users: "group",
    log_bikes: "directions_bike",
    log_services: "build",
    log_bookings: "calendar_month",
    log_reviews: "star",
    log_elapsed_time: "schedule",
    log_create: "construction"
  }
};

/**
 * Color configuration for icons - optimized for light and dark modes
 */
export const iconColors = {
  // Status colors
  success: {
    dark: '#4A8C18', // Green - visible on dark background
    light: '#1A2811' // Darker green - visible on light background
  },
  error: {
    dark: '#f87171', // Light red - visible on dark background
    light: '#dc2626' // Darker red - visible on light background
  },
  warning: {
    dark: '#fbbf24', // Light yellow/orange - visible on dark background
    light: '#d97706' // Darker orange - visible on light background
  },
  info: {
    dark: '#60a5fa', // Light blue - visible on dark background
    light: '#2563eb' // Darker blue - visible on light background
  },
  
  // Primary actions
  primary: {
    dark: '#ffffff', // White - visible on dark background
    light: '#1a1a1a' // Dark - visible on light background
  },
  secondary: {
    dark: 'rgba(255, 255, 255, 0.7)', // Semi-transparent white
    light: 'rgba(0, 0, 0, 0.7)' // Semi-transparent black
  },
  muted: {
    dark: 'rgba(255, 255, 255, 0.5)', // Muted white
    light: 'rgba(0, 0, 0, 0.5)' // Muted black
  },
  
  // Brand colors
  brand: {
    dark: '#4A8C18', // Brand green
    light: '#1A2811' // Darker brand green
  },
  
  // Navigation
  nav_active: {
    dark: '#4A8C18', // Brand green
    light: '#1A2811' // Darker brand green
  },
  nav_inactive: {
    dark: 'rgba(255, 255, 255, 0.6)', // Semi-transparent white
    light: 'rgba(0, 0, 0, 0.6)' // Semi-transparent black
  }
};

/**
 * Get icon name by category and key
 */
export const getIcon = (category, key) => {
  return iconMap[category]?.[key] || 'help';
};

/**
 * Get icon color for current theme
 */
export const getIconColor = (colorType, darkMode = true) => {
  const colorConfig = iconColors[colorType];
  if (!colorConfig) {
    return darkMode ? iconColors.primary.dark : iconColors.primary.light;
  }
  return darkMode ? colorConfig.dark : colorConfig.light;
};

/**
 * Flatten icon map for easy access
 */
export const icons = {
  // Navigation
  home: getIcon('navigation', 'home_dashboard'),
  admin: getIcon('navigation', 'admin_panel_super_admin'),
  settings: getIcon('navigation', 'settings'),
  profile: getIcon('navigation', 'profile'),
  calendar: getIcon('navigation', 'calendar_book_service'),
  bikes: getIcon('navigation', 'bikes_and_services'),
  shops: getIcon('navigation', 'service_shops'),
  bikePassports: getIcon('navigation', 'my_bike_passports'),
  timeOff: getIcon('navigation', 'time_off'),
  billing: getIcon('navigation', 'billing'),
  help: getIcon('navigation', 'help'),
  support: getIcon('navigation', 'support'),
  inventory: 'inventory_2',
  logout: getIcon('navigation', 'logout'),
  lightMode: getIcon('navigation', 'light_mode'),
  darkMode: getIcon('navigation', 'dark_mode'),
  
  // Status
  success: getIcon('status_feedback', 'success'),
  error: getIcon('status_feedback', 'error_inactive'),
  warning: getIcon('status_feedback', 'warning'),
  info: getIcon('status_feedback', 'info'),
  celebration: getIcon('status_feedback', 'success_completion'),
  loading: getIcon('status_feedback', 'in_progress_uploading_processing'),
  shield: getIcon('status_feedback', 'security_trial_protection'),
  
  // Bike & Service
  bike: getIcon('bike_service_domain', 'bike'),
  service: getIcon('bike_service_domain', 'service_repair'),
  technician: getIcon('bike_service_domain', 'technician_mechanic'),
  details: getIcon('bike_service_domain', 'details_clipboard'),
  documents: getIcon('bike_service_domain', 'documents_receipts'),
  photos: getIcon('bike_service_domain', 'photos'),
  verified: getIcon('bike_service_domain', 'security_ownership_verification'),
  key: getIcon('bike_service_domain', 'login_credentials_password'),
  lock: getIcon('bike_service_domain', 'lock_security'),
  
  // Communication
  chat: getIcon('communication_notifications', 'messages_chat'),
  email: getIcon('communication_notifications', 'email_sync_campaigns'),
  notifications: getIcon('communication_notifications', 'notifications_reminders'),
  send: getIcon('communication_notifications', 'send_email'),
  
  // Business
  analytics: getIcon('business_admin', 'analytics_statistics'),
  trendingUp: getIcon('business_admin', 'statistics_up_assumed'),
  trendingDown: getIcon('business_admin', 'statistics_down_assumed'),
  users: getIcon('business_admin', 'users_customers'),
  companies: getIcon('business_admin', 'companies'),
  campaign: getIcon('business_admin', 'marketing_campaigns'),
  payment: getIcon('business_admin', 'payment_billing'),
  
  // Calendar
  calendarMonth: getIcon('calendar_time', 'calendar'),
  event: getIcon('calendar_time', 'calendar_alternative'),
  dateRange: getIcon('calendar_time', 'calendar_date_picker'),
  schedule: getIcon('calendar_time', 'time_duration_buffer'),
  
  // Reviews
  star: getIcon('reviews_results', 'rating_star'),
  stars: getIcon('reviews_results', 'featured_review_popular'),
  achievement: getIcon('reviews_results', 'top_rated_achievement'),
  achievements: getIcon('reviews_results', 'top_rated_achievement'),
  perfect: getIcon('reviews_results', 'perfect_score'),
  
  // Marketing
  marketing: getIcon('business_admin', 'marketing_campaigns'),
  
  // Actions
  refresh: 'refresh',
  edit: getIcon('actions_operations', 'edit'),
  write: getIcon('actions_operations', 'write_signature_review'),
  view: getIcon('actions_operations', 'view'),
  play: getIcon('actions_operations', 'start_play'),
  upload: getIcon('actions_operations', 'upload_upgrade'),
  download: getIcon('actions_operations', 'download_assumed'),
  delete: getIcon('actions_operations', 'delete'),
  undo: getIcon('actions_operations', 'resume_restore'),
  swap: getIcon('actions_operations', 'switch_toggle'),
  copy: getIcon('actions_operations', 'copy'),
  
  // Features
  rocket: getIcon('features_services', 'launch_start_app'),
  target: getIcon('features_services', 'target_try_it_now'),
  integration: getIcon('features_services', 'integration'),
  empty: getIcon('features_services', 'empty_state'),
  building: getIcon('features_services', 'building_creating'),
  
  // Misc
  map: getIcon('misc', 'map'),
  location: getIcon('misc', 'location'),
  terrain: getIcon('misc', 'elevation_gain'),
  favorite: getIcon('misc', 'heart_rate'),
  tag: getIcon('misc', 'tag_discount_label'),
  palette: getIcon('misc', 'design_system_documentation'),
  phone: getIcon('misc', 'phone'),
  
  // Search
  search: 'search'
};
